// Light / dark mode.
//
// The colours live in theme.css: `:root` is light, `.dark` on <html> is dark.
// This module picks which one is active and remembers the choice per browser.
// "system" follows the device setting. index.html runs a small copy of
// applyTheme before the app loads so the page doesn't flash the wrong colours.

import { useEffect, useState } from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'poppin-hub-theme'
const THEME_COLORS = { light: '#fafafa', dark: '#18181b' } // matches --background
const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)')

export function getThemePreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved
  } catch {
    // Storage blocked (private mode); fall through to the default.
  }
  return 'system'
}

function applyTheme(preference: ThemePreference) {
  const dark = preference === 'dark' || (preference === 'system' && darkQuery().matches)
  const root = document.documentElement
  root.classList.toggle('dark', dark)
  root.style.colorScheme = dark ? 'dark' : 'light'
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? THEME_COLORS.dark : THEME_COLORS.light)
}

const listeners = new Set<(preference: ThemePreference) => void>()

export function setThemePreference(preference: ThemePreference) {
  try {
    localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    // Not saved, but still applied for this visit.
  }
  applyTheme(preference)
  listeners.forEach(listener => listener(preference))
}

/** Current preference plus a setter. Also keeps "system" in step with the device. */
export function useTheme() {
  const [preference, setPreference] = useState(getThemePreference)

  useEffect(() => {
    listeners.add(setPreference)
    const query = darkQuery()
    const onSystemChange = () => { if (getThemePreference() === 'system') applyTheme('system') }
    // Another tab changed the setting.
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return
      const next = getThemePreference()
      applyTheme(next)
      setPreference(next)
    }
    query.addEventListener('change', onSystemChange)
    window.addEventListener('storage', onStorage)
    return () => {
      listeners.delete(setPreference)
      query.removeEventListener('change', onSystemChange)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  return { preference, setPreference: setThemePreference }
}
